// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Digimon Digital World Universe - Star Systems Configuration
 * 
 * A digital universe where data flows like cosmic energy.
 * Each system represents a different layer or area of the Digital World.
 */

export const starSystems = [
  // STARTING SYSTEM - File Island at center
  {
    id: 1600,
    name: 'File Island',
    type: 'station',
    description: 'The starting point for all DigiDestined. A peaceful island where young Digimon learn and grow.',
    distance: 0,
    coordinates: { x: 45, y: 50, z: 0 },
    threat: 'LOW',
    resources: 'HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade', 'weapons', 'upgrades', 'modules'],
    tradeType: 'agricultural',
    primaryColor: '#00ffff',
    secondaryColor: '#00ff00',
    hasRings: false,
    scanDescription: 'Digital energy readings are stable. Safe harbor for new arrivals to the Digital World.'
  },

  {
    id: 1601,
    name: 'Server Continent',
    type: 'planet',
    description: 'Vast digital continent housing the primary servers. Data streams flow like rivers across its surface.',
    distance: 15,
    coordinates: { x: 60, y: 55, z: 3 },
    threat: 'LOW',
    resources: 'VERY HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade'],
    tradeType: 'industrial',
    primaryColor: '#00ff00',
    secondaryColor: '#ff00ff',
    hasRings: false,
    scanDescription: 'Massive data processing centers detected. High-bandwidth connections to all other systems.'
  },

  {
    id: 1602,
    name: 'Folder Continent',
    type: 'planet',
    description: 'Organized digital realm where data is stored in vast hierarchical structures.',
    distance: 20,
    coordinates: { x: 30, y: 60, z: -5 },
    threat: 'LOW',
    resources: 'HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade', 'upgrades'],
    tradeType: 'luxury',
    primaryColor: '#ff00ff',
    secondaryColor: '#00ffff',
    hasRings: false,
    scanDescription: 'Highly organized data structures. Perfect filing system maintains order across the continent.'
  },

  {
    id: 1603,
    name: 'Network City',
    type: 'station',
    description: 'Bustling digital metropolis where data packets travel at light speed through fiber optic highways.',
    distance: 18,
    coordinates: { x: 55, y: 35, z: 8 },
    threat: 'MODERATE',
    resources: 'MEDIUM',
    services: ['refuel', 'repairs', 'trade', 'weapons', 'modules'],
    tradeType: 'station_advanced',
    primaryColor: '#00ffff',
    secondaryColor: '#00ff00',
    hasRings: false,
    scanDescription: 'High-speed data traffic detected. Major network hub connecting multiple digital domains.'
  },

  {
    id: 1604,
    name: 'Dark Area',
    type: 'planet',
    description: 'Corrupted digital realm where deleted data and virus Digimon gather. Approach with extreme caution.',
    distance: 25,
    coordinates: { x: 40, y: 70, z: 2 },
    threat: 'VERY HIGH',
    resources: 'LOW',
    services: ['refuel', 'weapons'],
    tradeType: 'remote',
    primaryColor: '#ff00ff',
    secondaryColor: '#00ffff',
    hasRings: true,
    scanDescription: 'Corrupted data signatures detected. Viral activity high. Recommend enhanced firewall protection.'
  },

  {
    id: 1605,
    name: 'Kernel Sphere',
    type: 'station',
    description: 'Core processing station at the heart of the Digital World\'s operating system.',
    distance: 28,
    coordinates: { x: 65, y: 45, z: -3 },
    threat: 'HIGH',
    resources: 'VERY HIGH',
    services: ['refuel', 'repairs', 'upgrades', 'modules'],
    tradeType: 'station_advanced',
    primaryColor: '#00ff00',
    secondaryColor: '#ff00ff',
    hasRings: true,
    scanDescription: 'Critical system processes running. Core kernel functions maintain Digital World stability.'
  },

  {
    id: 1606,
    name: 'Firewall Fortress',
    type: 'station',
    description: 'Heavily defended digital fortress protecting the Digital World from external threats.',
    distance: 22,
    coordinates: { x: 25, y: 45, z: 10 },
    threat: 'MODERATE',
    resources: 'MEDIUM',
    services: ['refuel', 'repairs', 'weapons', 'upgrades'],
    tradeType: 'military',
    primaryColor: '#ff00ff',
    secondaryColor: '#00ff00',
    hasRings: false,
    scanDescription: 'Advanced security protocols active. Multiple layers of protection detected.'
  },

  {
    id: 1607,
    name: 'Data Stream Nexus',
    type: 'station',
    description: 'Central hub where all data streams converge. Information flows like cosmic rivers.',
    distance: 20,
    coordinates: { x: 50, y: 65, z: -8 },
    threat: 'LOW',
    resources: 'HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade', 'modules'],
    tradeType: 'station_advanced',
    primaryColor: '#00ffff',
    secondaryColor: '#ff00ff',
    hasRings: false,
    scanDescription: 'Massive data throughput detected. Primary information exchange point for the Digital World.'
  },

  {
    id: 1608,
    name: 'Recycle Bin',
    type: 'planet',
    description: 'Digital graveyard where deleted files and abandoned programs drift in digital limbo.',
    distance: 30,
    coordinates: { x: 35, y: 35, z: 5 },
    threat: 'HIGH',
    resources: 'LOW',
    services: ['refuel', 'repairs'],
    tradeType: 'remote',
    primaryColor: '#00ff00',
    secondaryColor: '#00ffff',
    hasRings: false,
    scanDescription: 'Fragmented data signatures. Deleted files may contain valuable information or dangerous viruses.'
  },

  {
    id: 1609,
    name: 'Cloud Storage',
    type: 'station',
    description: 'Vast digital storage facility floating in the digital ether. Infinite capacity for data.',
    distance: 26,
    coordinates: { x: 60, y: 30, z: -10 },
    threat: 'LOW',
    resources: 'VERY HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade', 'upgrades', 'modules'],
    tradeType: 'luxury',
    primaryColor: '#ff00ff',
    secondaryColor: '#00ff00',
    hasRings: true,
    scanDescription: 'Unlimited storage capacity detected. Backup systems ensure data integrity across all platforms.'
  },

  // DIGITAL SINGULARITY - The ultimate digital anomaly
  {
    id: 1699,
    name: 'Digital Singularity',
    type: 'blackhole',
    description: 'The source code of reality itself. Where all digital existence originates and terminates.',
    distance: 26000,
    coordinates: { x: 42, y: 48, z: -12 },
    threat: 'EXTREME',
    resources: 'INFINITE DATA',
    services: [],
    tradeType: 'none',
    primaryColor: '#000000',
    secondaryColor: '#00ffff',
    hasRings: true,
    scanDescription: 'Incomprehensible data density. Reality.exe appears to be running from this location.'
  }
];

// Star positions for map visualization
export const starPositions = starSystems.reduce((acc, system) => {
  acc[system.id] = system.coordinates;
  return acc;
}, {});